import {Injectable} from '@angular/core';
import {Observable, Subject, throwError} from 'rxjs';
import {catchError, finalize, map} from 'rxjs/operators';

import {
  BBS_API_URL,
  BBS_LIST_IN_API_URL,
  BBS_PAGE_API_URL,
  CONTENT_TYPE_JSON_UTF8,
  GUKEO_API_URL,
  PAGE
} from "../../defines/protocol";
import {Article} from '../../models/article.model';
import {IsEmptyString} from "../../defines/common.func";
import {SearchParam} from '../../defines/client.const';
import {ARTICLE_PAGE_SIZE} from "../../defines/desktop.const";
import {generateUrlWithQueryParams, getQueryParamsFromUrl, NormalizeSearchKeyword} from "../../defines/client.func";
import {PageLoader} from "../../interfaces/page-loader";
import {ApiService} from '../api/api.service';


@Injectable({
  providedIn: 'root'
})
export class BoardService implements PageLoader<Article, number> {
  private readonly urlApi = GUKEO_API_URL + BBS_PAGE_API_URL;

  protected curPage: number | null = null;
  protected totalCount: number | null = null;
  protected listArticle: Array<Article> | null = null;
  protected mapArticle: Map<number, Article> | null = null;
  protected nextUrl: string | null = null;
  protected prevUrl: string | null = null;

  protected load$: Subject<Array<Article>> | null = null;
  private loadDetail$: Observable<Article> | null = null;
  protected loadListIn$: Observable<Array<Article>> | null = null;


  constructor(
    private api: ApiService,
  ) { }

  /******************************************************************************
   * PageLoader<Article> 구현부
   *****************************************************************************/
  public getList(): Array<Article> | null {
    return this.listArticle;
  }
  public getCurPage(): number | null {
    return this.curPage;
  }
  public getTotalCount(): number | null {
    return this.totalCount;
  }
  public IsFirstPage(): boolean {
    return IsEmptyString(this.prevUrl);
  }
  public IsLastPage(): boolean {
    return IsEmptyString(this.nextUrl);
  }
  public IsLoading(): boolean {
    return this.load$ !== null;
  }
  public getById(article_no: number): Article | null {
    return this.mapArticle?.get(article_no) ?? null;
  }

  public ResetList(param?: SearchParam): Observable<number> {
    this.listArticle = null;
    this.mapArticle = null;
    this.totalCount = null;
    this.nextUrl = this.urlApi;
    this.prevUrl = null;
    this.nextUrl = null;

    return new Observable<number>(observer => {

      if (param) {
        const keyword = NormalizeSearchKeyword(param.keyword);
        this.nextUrl = generateUrlWithQueryParams(
          this.urlApi, {
            page_size: ARTICLE_PAGE_SIZE,
            ...('bbs_cat' in param ? {cat: param.bbs_cat} : {}),
            ...(keyword ? {search: keyword} : {}),
            ...('page' in param ? {page: param.page} : {}),
          });
      }
      else {
        this.nextUrl = this.urlApi;
      }
      console.log('BoardService::ResetList() nextUrl =', this.nextUrl);

      observer.next(1);
      observer.complete();
    });
  }


  public GetNextList(): Observable<Array<Article>> {

    if (!this.nextUrl) {
      return throwError({error: 'BoardService::GetNextList() - No more next pages in ArticleList'});
    }

    const nextUrl = this.nextUrl;
    return this.getUrlList(nextUrl).pipe(
      finalize(
        () => {
          const param = getQueryParamsFromUrl(<string>nextUrl);
          this.curPage = 'page' in param ? parseInt(param['page'], 10) : 1;
        }
      )
    );
  }

  public GetPreList(): Observable<Array<Article>> {

    if (this.prevUrl === null) {
      return throwError({error: 'BoardService::GetNextList() - No more pre pages in ArticleList'});
    }

    const prevUrl = this.prevUrl;
    return this.getUrlList(prevUrl).pipe(
      finalize(
        () => {
          const param = getQueryParamsFromUrl(<string>prevUrl);
          this.curPage = 'page' in param ? parseInt(param['page'], 10) : 1;
        }
      )
    );
  }

  protected getUrlList(url: string): Observable<Array<Article>> {
    // console.log('ArticleService::getUrlList()', urls);

    if (this.load$) {
      return this.load$;
    }

    this.load$ = new Subject<Array<Article>>();

    this.api.Get(url).pipe(
      catchError(err => {
        if (err?.status === 404) {
          this.totalCount = 0;
        }
        throw err;
      }),
      finalize(() => {
        this.load$ = null;
      } ),
      map((data: PAGE) => {
        console.log('ArticleService::getUrlList.api.Get data =', data);

        if (this.totalCount === null) {
          this.totalCount = data.count;
        }
        else if (this.totalCount !== data.count) {
          console.error('[error]' + this.urlApi, data );
        }

        this.listArticle = new Array<Article>();
        this.mapArticle = new Map<number, Article>();

        data.results.forEach(value => {
          const article = new Article(value);
          this.listArticle?.push(article);
          this.mapArticle?.set(article.no, article);
        });

        this.nextUrl = data.next ?? null;
        this.prevUrl = data.previous ?? null;

        return this.listArticle;
      })
    ).subscribe(this.load$);

    return this.load$.asObservable();
  }


  /**
   * TODO: 목록하고 상세 정보하고 동시에 로딩하면 문제가 생김.
   * @param no
   * @constructor
   */
  public GetDetail(no: number): Observable<Article>
  {
    const url = BBS_API_URL + no + '/';
    console.log('BoardService::GetDetail() url =', url);

    return this.loadDetail$ = this.api.Get(url).pipe(
      map(data => {
        return new Article(data);
      }),
      finalize(() => this.loadDetail$ = null)
    );
  }

  public GetListIn(ids: Array<number>): Observable<Array<Article>>
  {
    const url = BBS_LIST_IN_API_URL;
    console.log('BoardService::GetListIn() url =', url);

    return this.loadListIn$ = this.api.Post(url, {list: ids}, null, CONTENT_TYPE_JSON_UTF8).pipe(
      finalize(() => this.loadListIn$ = null),
      map((data: []) => {
        const list = new Array<Article>();
        data.forEach(value => list.push(new Article(value)));
        return list;
      }));
  }
}
